import Cookies from 'js-cookie'
import { create } from 'zustand'

import { accountMe } from '@/features/account/api'
import type { IAccount, IAccountAccess } from '@/features/account/type'
import type { OutletType } from '@/features/outlet/type'

import { queryClient } from '../configs/tanstack-query'

type State = {
	isAuthenticated: boolean
	data: IAccount | null
	outlet: {
		outletId: string
		outletName: string
		outletDefault: boolean
		outletType: OutletType
		teamId: string
		teamName: string
		isDefault: boolean
	}
}

interface Actions {
	login(token: string): Promise<State>
	logout(): Promise<void>
	revalidateSession(): Promise<void>

	// Outlet
	setOutlet(id: string | null): void
	revalidateOutlet(): void

	// Cookie
	getCookieToken(): string | null
	setCookieToken(token: string): void
	removeCookieToken(): void
}

const initialState: State = {
	isAuthenticated: false,
	data: null,
	outlet: null!,
}

const COOKIE_NAME = 'ikki.auth.token'

function accessToOutlet({
	outlet,
	isDefault,
	team,
}: IAccountAccess): State['outlet'] {
	return {
		outletId: outlet.id,
		outletName: outlet.name,
		outletType: outlet.type,
		outletDefault: isDefault,
		teamId: team.id,
		teamName: team.name,
		isDefault: isDefault,
	}
}

export const useAuth = create<State & Actions>((set, get) => ({
	...initialState,
	async login(token) {
		get().setCookieToken(token)
		const { account } = await queryClient.fetchQuery(accountMe.qry({}))
		set({
			isAuthenticated: true,
			data: account,
		})

		return get()
	},
	async revalidateSession() {
		const { getCookieToken } = get()
		const token = getCookieToken()

		if (token) {
			const data = await queryClient
				.ensureQueryData(accountMe.qry({}))
				.catch(() => null)

			if (data) {
				set({ isAuthenticated: true, data: data.account })
				get().revalidateOutlet()
				return
			} else {
				console.error('Revalidate session failed')
				get().logout()
			}
		}

		set(initialState)
	},
	async logout() {
		queryClient.clear()
		get().removeCookieToken()
		set(initialState)
	},

	// Outlet
	setOutlet(id) {
		if (id === null) set({ outlet: null! })

		const { data } = get()
		if (!data) throw new Error('Data is not available')

		const { access } = data
		const outlet = access.find(({ outlet }) => outlet.id === id)
		if (!outlet) throw new Error('Outlet not found')

		set({ outlet: accessToOutlet(outlet) })
	},
	revalidateOutlet() {
		const { outlet: _outlet, data } = get()
		if (!data || data.access.length === 0) return set({ outlet: null! })

		const { access } = data
		const { outletId } = _outlet || {}
		let outlet: IAccountAccess = null!

		const isValid = outletId && access.some((a) => a.outlet.id === outletId)
		if (isValid) return
		else {
			outlet = access.find((a) => a.isDefault) || access[0]
			set({ outlet: accessToOutlet(outlet) })
		}
	},

	// Cookie
	getCookieToken() {
		return Cookies.get(COOKIE_NAME) || null
	},
	setCookieToken(token) {
		Cookies.set(COOKIE_NAME, token)
	},
	removeCookieToken() {
		Cookies.remove(COOKIE_NAME)
	},
}))
